import React, { useState, useEffect, useCallback, useRef } from 'react';
import BlogNavbar from '../components/common/Navbar';
import BlogFooter from '../components/common/Footer';
import BlogHero from '../components/blogs/BlogHero';
import BlogCard from '../components/blogs/BlogCard';
import CategoryFilter from '../components/blogs/CategoryFilter';
import Pagination from '../../admin/components/common/Pagination'; // Import reusable pagination
import { BlogService } from '../../services/blogService';

import '../css/BlogPage.css';

const BlogPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(9); // Default 6 items per page
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination states
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  const searchTimeoutRef = useRef(null);

  // Debounce search term
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page when search changes
    }, 500);
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Fetch categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await BlogService.getAllBlogCategory();
        const categoriesData = response.data || response;
        setCategories(categoriesData);
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
    };
    
    fetchCategories();
  }, []);

  // Fetch blogs based on page, pageSize, category, and search
  const fetchBlogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await BlogService.getPaginatedBlogs(
        currentPage,
        pageSize,
        selectedCategoryId,
        debouncedSearchTerm
      );
      
      if (response.success) {
        setBlogs(response.data || []);
        if (response.pagination) {
          setTotalPages(response.pagination.totalPages);
          setTotalCount(response.pagination.totalCount);
        }
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch blogs');
      console.error('Error fetching blogs:', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, selectedCategoryId, debouncedSearchTerm]);

  // Fetch blogs when dependencies change
  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  // Handle category change
  const handleCategoryChange = (categoryName, categoryId) => {
    setSelectedCategory(categoryName);
    setSelectedCategoryId(categoryId);
    setSearchTerm(''); // Clear search box
    setDebouncedSearchTerm(''); // Clear debounced search
    setCurrentPage(1); // Reset to first page
  };

  // Handle search input change
  const handleSearchChange = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
    
    // Reset category when searching
    if (term.trim()) {
      setSelectedCategory('All');
      setSelectedCategoryId(null);
    }
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top of blog section
    window.scrollTo({ 
      top: document.querySelector('.ts-blog-container')?.offsetTop - 100 || 0, 
      behavior: 'smooth' 
    });
  };

  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setSelectedCategory('All');
    setSelectedCategoryId(null);
    setCurrentPage(1);
  };

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <div className="ts-blog-grid">
      {[...Array(pageSize)].map((_, index) => (
        <div key={index} className="ts-blog-card-skeleton">
          <div className="ts-blog-card-skeleton-image"></div>
          <div className="ts-blog-card-skeleton-content">
            <div className="ts-blog-card-skeleton-title"></div>
            <div className="ts-blog-card-skeleton-text"></div>
            <div className="ts-blog-card-skeleton-text"></div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      {/* Hero Section - Always Visible */}
      <BlogHero 
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        selectedCategory={selectedCategory}
        onCategoryChange={handleCategoryChange}
      />
      
      {/* Content Section - Loads separately */}
      <div className="ts-blog-container">
        {/* Category Filter Row */}
        <CategoryFilter 
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
          categories={categories}
        />
        
        {/* Results Count and Info */}
        <div className="ts-blog-results-count">
          {loading ? (
            <>Loading articles...</>
          ) : (
            <>
              {debouncedSearchTerm ? (
                <>Search results for "<strong>{debouncedSearchTerm}</strong>": {totalCount} articles found</>
              ) : selectedCategory !== 'All' ? (
                <>Showing articles in category "<strong>{selectedCategory}</strong>": {totalCount} total</>
              ) : (
                <>Showing {blogs.length} of {totalCount} articles</>
              )}
            </>
          )}
        </div>

        {/* Error State */}
        {error && (
          <div className="ts-blog-error">
            <h3>Something went wrong</h3>
            <p>{error}</p>
            <button onClick={() => fetchBlogs()} className="ts-blog-retry-btn">
              Try Again
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <LoadingSkeleton />
        )}

        {/* Blog Grid - Show when not loading */}
        {!loading && !error && (
          <>
            {blogs.length > 0 ? (
              <div className="ts-blog-grid">
                {blogs.map((blog, index) => (
                  <BlogCard 
                    key={blog.id} 
                    blog={{
                      ...blog,
                      category: blog.category || blog.Category
                    }} 
                    index={index} 
                  />
                ))}
              </div>
            ) : (
              <div className="ts-blog-no-results">
                <h3>No posts found</h3>
                <p>
                  {debouncedSearchTerm 
                    ? `No articles match "${debouncedSearchTerm}". Try a different search term.`
                    : selectedCategory !== 'All'
                    ? `No articles found in "${selectedCategory}" category.`
                    : 'No articles available at the moment. Please check back later.'}
                </p>
                {(debouncedSearchTerm || selectedCategory !== 'All') && (
                  <button 
                    onClick={clearAllFilters}
                    className="ts-blog-clear-filters"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {/* Pagination - Using Reusable Component */}
        {!loading && !error && totalCount > 0 && (
          <div className="ts-blog-pagination-wrapper">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={[9, 12, 24]}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default BlogPage;